import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Email as EmailIcon, Refresh, Save as SaveIcon, Send } from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { mailSettingsApi } from '../services/api';
import {
  emptyMailSettingsFormData,
  MailSettingsFormData,
  MailSettingsRequest,
  mapMailSettingsToFormData,
} from '../types/mailSettings';
import { settingsCardSx } from '../settings/companySettingsShared';
import { MAIL_SETTINGS_QUERY_KEY, useMailSettingsQuery } from '../settings/useMailSettingsQuery';
import { SettingsPageHeader } from '../settings/SettingsPageHeader';

export const EmailSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;

  const [isDirty, setIsDirty] = useState(false);
  const [settingsData, setSettingsData] = useState<MailSettingsFormData>(emptyMailSettingsFormData);
  const [testRecipient, setTestRecipient] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const settingsQuery = useMailSettingsQuery(companyId);

  useEffect(() => {
    if (!settingsQuery.data) {
      return;
    }
    setSettingsData(mapMailSettingsToFormData(settingsQuery.data));
    setIsDirty(false);
  }, [settingsQuery.data]);

  const updateSettingsMutation = useMutation(
    (data: MailSettingsRequest) => mailSettingsApi.updateSettings(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([MAIL_SETTINGS_QUERY_KEY, companyId]);
        toast.success(t('settings.messages.settingsUpdated', 'Settings updated successfully'));
        setIsDirty(false);
        setTestResult(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('settings.messages.settingsUpdateFailed', 'Failed to update settings'));
      },
    }
  );

  const testSendMutation = useMutation((recipient: string) => mailSettingsApi.sendTestEmail(recipient), {
    onSuccess: (response) => {
      setTestResult(response);
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || t('settings.messages.testEmailFailed', 'Failed to send test email');
      setTestResult({ success: false, message });
      toast.error(message);
    },
  });

  const handleFieldChange = <K extends keyof MailSettingsFormData>(field: K, value: MailSettingsFormData[K]) => {
    setSettingsData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSaveSettings = () => {
    const payload: MailSettingsRequest = {
      smtpHost: settingsData.smtpHost.trim(),
      smtpPort: settingsData.smtpPort,
      smtpAuth: settingsData.smtpAuth,
      smtpSsl: settingsData.smtpSsl,
      smtpTls: settingsData.smtpTls,
      smtpVerifyHostCert: settingsData.smtpVerifyHostCert,
      active: settingsData.active,
      fromEmail: settingsData.fromEmail.trim(),
      fromName: settingsData.fromName.trim(),
      username: settingsData.username.trim(),
    };
    if (settingsData.password.trim()) {
      payload.password = settingsData.password.trim();
    }
    updateSettingsMutation.mutate(payload);
  };

  const handleSendTestEmail = () => {
    if (!testRecipient.trim()) {
      toast.error(t('settings.emailSettings.recipientRequired', 'Enter a recipient email address'));
      return;
    }
    setTestResult(null);
    testSendMutation.mutate(testRecipient.trim());
  };

  if (settingsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const hasStoredPassword = Boolean(settingsQuery.data?.hasStoredPassword);
  const isConfigured = Boolean(settingsQuery.data?.configured);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <SettingsPageHeader
        icon={<EmailIcon color="primary" />}
        title={t('settings.emailSettingsTitle', 'Email Settings')}
        subtitle={t(
          'settings.emailSettingsSubtitle',
          'Connect the SMTP server RentMan uses to send emails, then send a test email to confirm the connection works.'
        )}
        actions={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ width: { xs: '100%', lg: 'auto' } }}
          >
            <Tooltip title={t('common.refresh', 'Refresh')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => settingsQuery.refetch()}
                  sx={{ minWidth: 150 }}
                >
                  {t('common.refresh', 'Refresh')}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={updateSettingsMutation.isLoading ? <CircularProgress size={18} /> : <SaveIcon />}
              onClick={handleSaveSettings}
              disabled={!isDirty || updateSettingsMutation.isLoading}
              sx={{ minWidth: 180 }}
            >
              {t('common.saveChanges', 'Save Changes')}
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={settingsCardSx}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t('settings.emailSettings.serverTitle', 'Mail Server')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings.emailSettings.serverHint', 'The outgoing (SMTP) server details for sending emails.')}
            </Typography>

            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label={t('settings.emailSettings.smtpHost', 'SMTP Host')}
                placeholder="smtp.gmail.com"
                value={settingsData.smtpHost}
                onChange={(e) => handleFieldChange('smtpHost', e.target.value)}
              />
              <TextField
                fullWidth
                type="number"
                label={t('settings.emailSettings.smtpPort', 'SMTP Port')}
                value={settingsData.smtpPort}
                onChange={(e) => handleFieldChange('smtpPort', Number(e.target.value) || 0)}
              />

              <Divider />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsData.smtpAuth}
                    onChange={(e) => handleFieldChange('smtpAuth', e.target.checked)}
                  />
                }
                label={t('settings.emailSettings.smtpAuth', 'Server requires authentication')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsData.smtpTls}
                    onChange={(e) => {
                      setIsDirty(true);
                      setSettingsData((prev) => ({ ...prev, smtpTls: e.target.checked, smtpSsl: e.target.checked ? false : prev.smtpSsl }));
                    }}
                  />
                }
                label={t('settings.emailSettings.smtpTls', 'Use STARTTLS (recommended, e.g. port 587)')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsData.smtpSsl}
                    onChange={(e) => {
                      setIsDirty(true);
                      setSettingsData((prev) => ({ ...prev, smtpSsl: e.target.checked, smtpTls: e.target.checked ? false : prev.smtpTls }));
                    }}
                  />
                }
                label={t('settings.emailSettings.smtpSsl', 'Use SSL (e.g. port 465)')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsData.smtpVerifyHostCert}
                    onChange={(e) => handleFieldChange('smtpVerifyHostCert', e.target.checked)}
                  />
                }
                label={t('settings.emailSettings.smtpVerifyHostCert', 'Verify server SSL certificate')}
              />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={settingsCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t('settings.emailSettings.accountTitle', 'Mail Account')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsData.active}
                    onChange={(e) => handleFieldChange('active', e.target.checked)}
                  />
                }
                label={t('settings.emailSettings.active', 'Active')}
                sx={{ m: 0 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings.emailSettings.accountHint', 'The sender identity and login credentials used to send emails.')}
            </Typography>

            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label={t('settings.emailSettings.fromEmail', 'From Email')}
                placeholder="rentals@yourcompany.com"
                value={settingsData.fromEmail}
                onChange={(e) => handleFieldChange('fromEmail', e.target.value)}
              />
              <TextField
                fullWidth
                label={t('settings.emailSettings.fromName', 'From Name')}
                placeholder="RentMan"
                value={settingsData.fromName}
                onChange={(e) => handleFieldChange('fromName', e.target.value)}
              />
              <TextField
                fullWidth
                label={t('settings.emailSettings.username', 'Username')}
                helperText={t('settings.emailSettings.usernameHint', 'Usually the same as your email address.')}
                value={settingsData.username}
                onChange={(e) => handleFieldChange('username', e.target.value)}
              />
              <TextField
                fullWidth
                type="password"
                label={t('settings.emailSettings.password', 'Password')}
                placeholder={hasStoredPassword ? '••••••••' : ''}
                helperText={
                  hasStoredPassword
                    ? t('settings.emailSettings.passwordHint', 'Leave blank to keep the currently saved password.')
                    : t(
                        'settings.emailSettings.passwordHintGmail',
                        'For Gmail, use an App Password, not your regular account password.'
                      )
                }
                value={settingsData.password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
              />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={settingsCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <Send color={isConfigured ? 'primary' : 'disabled'} />
              <Typography variant="h6">{t('settings.emailSettings.testTitle', 'Send Test Email')}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t(
                'settings.emailSettings.testHint',
                'Save your settings, then send a test email to confirm the connection works.'
              )}
            </Typography>

            {isDirty && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('settings.emailSettings.unsavedWarning', 'You have unsaved changes. Save them before sending a test email.')}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                fullWidth
                label={t('settings.emailSettings.testRecipient', 'Recipient Email')}
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                sx={{ maxWidth: { sm: 360 } }}
              />
              <Button
                variant="contained"
                startIcon={testSendMutation.isLoading ? <CircularProgress size={18} /> : <Send />}
                onClick={handleSendTestEmail}
                disabled={isDirty || testSendMutation.isLoading}
                sx={{ minWidth: 180 }}
              >
                {t('settings.emailSettings.sendTest', 'Send Test Email')}
              </Button>
            </Stack>

            {testResult && (
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                {testResult.message}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {settingsQuery.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {t('common.error', 'Error')}
        </Alert>
      )}
    </Container>
  );
};
